import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  RdsAccordionModule,
  RdsProgressSpinnerModule,
} from '@rds/angular-components';
import { AssistantHandoverService } from '../../../services/assistant-handover.service';
import { AssistantService } from '../../../services/assistant.service';
import { OfferingsService } from '../../../services/offerings.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { MarkdownPipe } from '../../../pipes/markdown.pipe';
import { SearchInputComponent } from '../../shared/search-input/search-input.component';
import { SparkleIconComponent } from '../../shared/sparkle-icon/sparkle-icon.component';
import { OfferingLinkDirective } from '../../../directives/offering-link.directive';
import { offeringLinkMap } from '../../../util/offering-mention.util';
import { ASSISTANT_ACTIONS_FLAG } from '../../../constants/assistant.constants';

/**
 * Assistant panel: a streamed take on whatever the page is about, plus a way
 * into the full conversation.
 *
 * Used above the Sinequa result list and at the top of an offering page. It
 * knows nothing about either — the parent supplies the prompt to summarise and,
 * optionally, the subject to carry into the chat.
 *
 * It never renders the results themselves. The agent's `search_offerings` tool
 * hits the same Sinequa app and query the SPA does, but its output never
 * reaches the browser: the SSE protocol carries `token`/`tool`/`done`/`error`,
 * and `tool` names the tool without its payload.
 */
@Component({
  selector: 'inexp-assistant-widget',
  imports: [
    RdsAccordionModule,
    RdsProgressSpinnerModule,
    MarkdownPipe,
    SearchInputComponent,
    SparkleIconComponent,
    OfferingLinkDirective,
  ],
  templateUrl: './assistant-widget.component.html',
  styleUrl: './assistant-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantWidgetComponent {
  private readonly _assistant = inject(AssistantService);
  private readonly _offerings = inject(OfferingsService);
  private readonly _handover = inject(AssistantHandoverService);
  private readonly _router = inject(Router);

  /**
   * What to ask the agent for. Empty means no call — which is how a results
   * page with no query, or an offering page still loading, stays quiet.
   *
   * On the results page this is bound to a topic that only moves on a real text
   * search: filter and pagination navigations are swallowed upstream by
   * `isInternalNavigation`, so refining does not spend another agent turn.
   */
  readonly summaryPrompt = input<string>('');

  /**
   * What the user was looking at, as a phrase the assistant page can drop
   * straight into a sentence — `the "Grafana Cloud" offering`, `the search for
   * "observability"`. The parent composes it because only the parent knows what
   * kind of thing it is; the widget just carries it across.
   */
  readonly followUpContext = input<string>('');

  /** Placeholder for the follow-up field; differs per surface. */
  readonly followUpPlaceholder = input<string>(
    'Ask a follow-up about these results...',
  );

  /**
   * The summary's question as a reader would put it, for the chat's transcript.
   *
   * Deliberately NOT `summaryPrompt`, which is written about the reader in the
   * third person and carries directives for the model — "A user is browsing the
   * Panorama offering catalog for X… Do not list every offering". Rendering that
   * as their own turn would be a lie, and sending it as history would teach the
   * model that the reader talks like a prompt.
   *
   * The search page has the real thing: what they typed in the hero. The
   * offering page has nothing, because they opened a page rather than asking,
   * so it composes one.
   */
  readonly openingQuestion = input<string>('');

  readonly isExpanded = signal(true);
  readonly summary = signal('');
  readonly isStreaming = signal(false);

  /** Set when the stream fails; the summary is dropped, the ask row stays. */
  readonly hasFailed = signal(false);

  /** Same switch as the chat's action row — one feature, one flag. */
  private readonly _linksEnabled = inject(FeatureFlagService).createFlagSignal(
    ASSISTANT_ACTIONS_FLAG,
  );

  /**
   * Offering names to link inside the settled summary.
   *
   * Held back while the text is still arriving. The match runs against the whole
   * catalogue, so redoing it on every token would be the most expensive thing on
   * the page, and a half-arrived name flickering into a link reads as a glitch.
   */
  readonly summaryLinks = computed(() =>
    this.isStreaming() || !this._linksEnabled()
      ? undefined
      : offeringLinkMap(this.summary(), this._offerings.offerings()),
  );

  constructor() {
    effect((onCleanup) => {
      const prompt = this.summaryPrompt().trim();

      untracked(() => {
        this.summary.set('');
        this.hasFailed.set(false);
        this.isStreaming.set(!!prompt);
      });

      if (!prompt) {
        return;
      }

      const sub = this._assistant.streamChat(prompt).subscribe({
        next: (event) => {
          switch (event.type) {
            case 'token':
              this.summary.update((text) => text + event.text);
              break;
            case 'done':
              this.isStreaming.set(false);
              break;
            case 'error':
              this._fail();
              break;
          }
        },
        error: () => this._fail(),
      });

      // Runs on the next prompt and on destroy, so a superseded stream is
      // dropped rather than racing the new one into the same signal.
      onCleanup(() => sub.unsubscribe());
    });
  }

  /**
   * Take a question to the full chat, with the conversation so far.
   *
   * An empty box still opens the chat, just with nothing asked yet. `context`
   * is dropped along with the question: the chat folds context into an opening
   * prompt and ignores it without one, so carrying it would only put a
   * parameter in the URL that nothing reads.
   */
  ask(prompt: string): void {
    // Offered even when the box is empty: opening the chat to carry on reading
    // is as much a continuation as asking something is.
    this._handOver();

    const q = prompt.trim();
    if (!q) {
      this._router.navigate(['/assistant']);
      return;
    }
    const context = this.followUpContext().trim();
    this._router.navigate(['/assistant'], {
      queryParams: context ? { q, context } : { q },
    });
  }

  /**
   * Give the chat the exchange the reader has already had, if there is one.
   *
   * Only a settled answer counts. Mid-stream text is not an answer the reader
   * has read, and a failed turn has none — handing either over would open the
   * chat on a half-sentence the model would then be asked to treat as its own.
   */
  private _handOver(): void {
    const question = this.openingQuestion().trim();
    const answer = this.summary().trim();
    if (!question || !answer || this.isStreaming() || this.hasFailed()) {
      return;
    }
    this._handover.offer({ question, answer });
  }

  private _fail(): void {
    this.hasFailed.set(true);
    this.summary.set('');
    this.isStreaming.set(false);
  }
}
